import { LOCALES, RTL_LOCALES } from '@massar/shared/constants';

/**
 * Everything that depends on *which* language is active, in one place (docs/04 §5).
 *
 * This module exists because it did not, and the cost showed: adding Kurdish Sorani meant
 * finding twenty-eight separate `i18n.language === 'ar' ? … : …` ternaries, each of which
 * silently treated "not Arabic" as "English". A third language turns every one of those
 * into a bug — Sorani would have rendered left-to-right with English month names while the
 * page around it was right-to-left.
 *
 * So: no component decides anything about a locale itself. It asks here.
 */

export { LOCALES, RTL_LOCALES };

/**
 * BCP 47 tag for `Intl`, with a deliberate override.
 *
 * **Numerals stay Western-Arabic in both RTL languages** (`-u-nu-latn`). An accountant
 * reading a trial balance in Arabic or Sorani still expects `1,234.00`, and mixing numeral
 * systems down a column destroys the tabular alignment the whole grid depends on. The
 * *text* flips; the figures do not.
 *
 * Sorani maps to `ckb-IQ`: `Intl` has no Sorani-specific number or date data, so it falls
 * back to a sensible regional default rather than throwing on an unknown tag.
 */
const TAGS = {
  en: 'en-US',
  ar: 'ar-AE-u-nu-latn',
  ckb: 'ckb-IQ-u-nu-latn',
};

export const localeTag = (locale) => TAGS[locale] ?? TAGS.en;

export const isRtl = (locale) => RTL_LOCALES.includes(locale);

export const dirFor = (locale) => (isRtl(locale) ? 'rtl' : 'ltr');

/**
 * The name fields a record carries, in the order this locale should prefer them.
 *
 * Sorani falls through to **English**, not Arabic. Same script, different language: showing
 * an Arabic name to a Kurdish reader is not a graceful degradation, it is the wrong words —
 * and English is the neutral default the rest of the app already falls back to.
 */
/**
 * Re-exported from `packages/shared` (docs/10 §23).
 *
 * It used to live here, and the server therefore did not have it — so every export printed
 * the English name even for an Arabic reader whose `nameAr` was on the document. Moving the
 * one implementation to the shared package is what lets a PDF and the screen it came from
 * agree about what something is called.
 */
import { localizedName } from '@massar/shared/i18n/localizedName';

export { localizedName };

export default { localeTag, isRtl, dirFor, localizedName, LOCALES, RTL_LOCALES };
